import { StringUtility } from "@common/utils";
import { ClientModel, IClientModel } from "./ClientModel";
import { IModel, Model } from "./Model";
import { IPetModel, PetModel } from "./PetModel";
import { ISpeciesModel, SpeciesModel } from "./SpeciesModel";
import { DateTime } from "luxon";
import { SourceSystem } from "@common/models/Enums/SourceSystem";

export interface IReservationModel extends IModel {
    reservationId: number;
    clinicId: number;
    speciesId: number;
    clientId: number | undefined;
    petId: number | undefined;
    reservedDate: string;
    confirmationDate: string | undefined;
    startDate: string;
    endDate: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    phone1: string;
    petName: string;
    sourceSystemKey: string;
    wasPriorCustomer: boolean;
    cancellationDate: string | undefined;
    confirmation1SMSDate: string | undefined;
    confirmation2SMSDate: string | undefined;
    confirmation3SMSDate: string | undefined;
    isHidden: boolean;
    source: string;
    preVisitSMSDate: string | undefined;
    preVisitSMSResponseDate: string | undefined;
    preVisitSMSResponse: boolean | undefined;
    addedToCalendar: boolean;
    rebooked: boolean;
    bookingSMSDate: string | undefined;
    // clinic: IClinicModel;  // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
    species: ISpeciesModel;
    client: IClientModel | undefined;
    pet: IPetModel | undefined;
    intakeId: number | undefined;
    intakeResponseCompleteDate: string | undefined;
    intakePetId: number | undefined;
    intakePetResponseCompleteDate: string | undefined;
    intakePublicId: string | undefined;
    clinicCancellationProcessedDate: string | undefined;
    reservationPublicId: string | undefined;
    hasVisit: boolean;
    notes: string | undefined;
    reasonForVisit: string | undefined;
    reservationAssignmentStatusId: number | undefined;
    canCall: boolean;
    canSMS: boolean;
}

export class ReservationModel extends Model implements Record<keyof IReservationModel, any> {
    reservationId: number;
    clinicId: number;
    speciesId: number;
    clientId: number | undefined;
    petId: number | undefined;
    reservedDate: string;
    confirmationDate: DateTime | undefined;
    startDate: string;
    endDate: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    phone1: string;
    petName: string;
    sourceSystemKey: string;
    wasPriorCustomer: boolean;
    cancellationDate: DateTime | undefined;
    confirmation1SMSDate: DateTime | undefined;
    confirmation2SMSDate: DateTime | undefined;
    confirmation3SMSDate: DateTime | undefined;
    isHidden: boolean;
    source: string;
    preVisitSMSDate: DateTime | undefined;
    preVisitSMSResponseDate: DateTime | undefined;
    preVisitSMSResponse: boolean | undefined;
    addedToCalendar: boolean;
    rebooked: boolean;
    bookingSMSDate: DateTime | undefined;
    species: SpeciesModel;
    client: ClientModel | undefined;
    pet: PetModel | undefined;
    intakeId: number | undefined;
    intakeResponseCompleteDate: DateTime | undefined;
    intakePetId: number | undefined;
    intakePetResponseCompleteDate: DateTime | undefined;
    intakePublicId: string | undefined;
    clinicCancellationProcessedDate: DateTime | undefined;
    reservationPublicId: string | undefined;
    hasVisit: boolean;
    notes: string | undefined;
    reasonForVisit: string | undefined;
    reservationAssignmentStatusId: number | undefined;
    canCall: boolean;
    canSMS: boolean;

    constructor(params: IReservationModel) {
        super(params)

        this.reservationId = params.reservationId
        this.clinicId = params.clinicId
        this.speciesId = params.speciesId
        this.clientId = StringUtility.SanitizeNumber(params.clientId)
        this.petId = StringUtility.SanitizeNumber(params.petId)
        this.reservedDate = params.reservedDate
        this.confirmationDate = params.confirmationDate ? StringUtility.SanitizeDateTimeISOAsUtc(params.confirmationDate) : undefined
        this.startDate = params.startDate
        this.endDate = params.endDate
        this.firstName = params.firstName
        this.lastName = params.lastName
        this.emailAddress = params.emailAddress
        this.phone1 = params.phone1
        this.petName = params.petName
        this.sourceSystemKey = params.sourceSystemKey
        this.wasPriorCustomer = params.wasPriorCustomer
        this.cancellationDate = StringUtility.SanitizeDateTimeISOAsUtc(params.cancellationDate)
        this.confirmation1SMSDate = StringUtility.SanitizeDateTimeISOAsUtc(params.confirmation1SMSDate)
        this.confirmation2SMSDate = StringUtility.SanitizeDateTimeISOAsUtc(params.confirmation2SMSDate)
        this.confirmation3SMSDate = StringUtility.SanitizeDateTimeISOAsUtc(params.confirmation3SMSDate)
        this.isHidden = params.isHidden
        this.source = params.source
        this.preVisitSMSDate = StringUtility.SanitizeDateTimeISOAsUtc(params.preVisitSMSDate)
        this.preVisitSMSResponseDate = StringUtility.SanitizeDateTimeISOAsUtc(params.preVisitSMSResponseDate)
        this.preVisitSMSResponse = params.preVisitSMSResponse
        this.addedToCalendar = params.addedToCalendar
        this.rebooked = params.rebooked
        this.bookingSMSDate = StringUtility.SanitizeDateTimeISOAsUtc(params.bookingSMSDate)
        this.species = new SpeciesModel(params.species)
        this.client = params.client ? new ClientModel(params.client) : undefined
        this.pet = params.pet ? new PetModel(params.pet) : undefined
        this.intakeId = StringUtility.SanitizeNumber(params.intakeId)
        this.intakeResponseCompleteDate = StringUtility.SanitizeDateTimeISOAsUtc(params.intakeResponseCompleteDate)
        this.intakePetId = StringUtility.SanitizeNumber(params.intakePetId)
        this.intakePetResponseCompleteDate = StringUtility.SanitizeDateTimeISOAsUtc(params.intakePetResponseCompleteDate)
        this.intakePublicId = StringUtility.SanitizeString(params.intakePublicId)
        this.clinicCancellationProcessedDate = StringUtility.SanitizeDateTimeISOAsUtc(params.clinicCancellationProcessedDate)
        this.reservationPublicId = StringUtility.SanitizeString(params.reservationPublicId)
        this.hasVisit = params.hasVisit
        this.notes = StringUtility.SanitizeString(params.notes) // "appointment notes" in the exam intake
        this.reasonForVisit = StringUtility.SanitizeString(params.reasonForVisit)
        this.reservationAssignmentStatusId = StringUtility.SanitizeNumber(params.reservationAssignmentStatusId)
        this.canCall = params.canCall
        this.canSMS = params.canSMS
    }

	isFollowUpReservation(): boolean {
		return this.source === SourceSystem[SourceSystem.PetPass].toLowerCase();
	}
}
